import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const getTriggerLabels = (t) => ({
  // ─── Violations (notifications_engine.py) ───
  violation_new_ban: t('admin.notificationsRules.triggerNewBan', 'New ban detected'),
  violation_ban_withdrawn: t('admin.notificationsRules.triggerBanRemoved', 'Ban removed / expired'),
  violation_status_changed: t('admin.notificationsRules.triggerStatusChange', 'Violation status changed'),
  // ─── Sessions (notifications_engine.py) ───
  session_hourly_recap: t('admin.notificationsRules.triggerHourlyRecap', 'Hourly diamonds recap'),
  // ─── System (notifications_engine.py) ───
  system_cookie_expired: t('admin.notificationsRules.triggerCookieExpired', 'Backstage cookie expired'),
  system_scraper_error: t('admin.notificationsRules.triggerScraperError', 'Scraper error'),
  // ─── Créateurs ───
  tier_rankup: t('admin.notificationsRules.triggerRankUp', 'Rank-up confirmed'),
  tier_lost: t('admin.notificationsRules.triggerTierLost', 'Tier lost'),
  inactive_days: t('admin.notificationsRules.triggerInactiveDays', 'Inactive X days'),
  activeness_close: t('admin.notificationsRules.triggerActivenessClose', 'Close to activeness tier'),
  contract_expiring: t('admin.notificationsRules.triggerContractExpiring', 'Contract expiring'),
  never_live: t('admin.notificationsRules.triggerNeverLive', 'Never been live'),
  // ─── Scrapers / Injections ───
  injection_done: t('admin.notificationsRules.triggerInjectionDone', 'Injection summary'),
  scraper_daily_success: t('admin.notificationsRules.triggerDailySuccess', 'Scraper daily — success'),
  scraper_daily_error: t('admin.notificationsRules.triggerDailyError', 'Scraper daily — error'),
  scraper_creators_success: t('admin.notificationsRules.triggerCreatorsSuccess', 'Scraper creators — success'),
  scraper_creators_error: t('admin.notificationsRules.triggerCreatorsError', 'Scraper creators — error'),
  scraper_violations_success: t('admin.notificationsRules.triggerViolationsSuccess', 'Scraper violations — success'),
  scraper_violations_error: t('admin.notificationsRules.triggerViolationsError', 'Scraper violations — error'),
  scraper_tickets_done: t('admin.notificationsRules.triggerTicketsDone', 'Auto-ticket — result'),
  scraper_violations_recap: t('admin.notificationsRules.triggerViolationsRecap', 'Daily violations recap'),
  // ─── Match & Tournois ───
  match_upcoming: t('admin.notificationsRules.triggerMatchUpcoming', 'Match in 30min'),
  match_completed: t('admin.notificationsRules.triggerMatchEnded', 'Match ended'),
  match_creator_offline: t('admin.notificationsRules.triggerNotLiveBefore', 'Creator not live before match'),
  tournament_participant_left: t('admin.notificationsRules.triggerTournamentLeft', 'Tournament participant left agency'),
  // ─── Manager ───
  manager_daily_recap: t('admin.notificationsRules.triggerManagerRecap', 'Manager daily recap'),
  // ─── Finance ───
  finance_rankup: t('admin.notificationsRules.triggerRankUp', 'Rank-up confirmed'),
  finance_cashback_tier: t('admin.notificationsRules.triggerCashbackTier', 'Cashback tier reached'),
  // ─── Créateurs extra ───
  session_inactive: t('admin.notificationsRules.triggerInactive3d', 'Inactive creator 3d+'),
  session_exceptional: t('admin.notificationsRules.triggerSessionExceptional', 'Exceptional session'),
  violation_account_suspended: t('admin.notificationsRules.triggerAccountSuspended', 'Account suspended'),
});

const VARIABLES_HINT = '{creator_username} {tier_current} {tier_m1} {bonus} {days} {next_level} {nb_creators} {nb_rankup} {manager_name} {total_violations} {new_violations} {total_tickets}';

const RuleModal = ({ rule, onClose, onSaved }) => {
  const { t } = useTranslation();
  const TRIGGER_LABELS = getTriggerLabels(t);
  const TRIGGER_OPTIONS = Object.entries(TRIGGER_LABELS)?.map(([value, label]) => ({ value, label }));
  const isEdit = !!rule?.id;
  const [form, setForm] = useState({
    name: rule?.name || '',
    trigger_type: rule?.trigger_type || 'tier_rankup',
    message_template: rule?.message_template || '',
    trigger_params: rule?.trigger_params || {},
    recipient: rule?.recipient || 'admin',
    active: rule?.active !== undefined ? rule?.active : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const triggerType = form?.trigger_type;
  const hasParam = ['inactive_days', 'activeness_close', 'contract_expiring', 'never_live']?.includes(triggerType);

  const getParamLabel = () => {
    if (triggerType === 'inactive_days') return 'Déclencher après X jours';
    if (triggerType === 'activeness_close') return 'Déclencher à X jours du palier';
    if (triggerType === 'contract_expiring') return 'Déclencher X jours avant';
    if (triggerType === 'never_live') return 'Déclencher après X jours sans live';
    return 'Paramètre';
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) { setError('Nom requis'); return; }
    setSaving(true);
    setError('');

    const payload = {
      name: form?.name,
      trigger_type: form?.trigger_type,
      message_template: form?.message_template,
      trigger_params: form?.trigger_params,
      recipient: form?.recipient,
      active: form?.active,
    };

    let err;
    if (isEdit) {
      const { error: e } = await supabase?.from('notification_rules')?.update(payload)?.eq('id', rule?.id);
      err = e;
    } else {
      const { error: e } = await supabase?.from('notification_rules')?.insert([payload]);
      err = e;
    }

    if (err) {
      setError(err?.message);
    } else {
      onSaved?.();
      onClose?.();
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-lg rounded-2xl border p-6 flex flex-col gap-5 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#111111', borderColor: '#2a2a2a' }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Sora, sans-serif' }}>
            {isEdit ? t('admin.notificationsRules.editRule', 'Edit rule') : t('admin.notificationsRules.newRule', 'New rule')}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {/* Name */}
          <div>
            <label className="text-white/60 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Nom de la règle</label>
            <input
              value={form?.name}
              onChange={e => setForm(p => ({ ...p, name: e?.target?.value }))}
              placeholder="Ex: Alerte rank-up MENA"
              className="w-full px-3 py-2.5 rounded-lg text-base text-white outline-none border transition-colors"
              style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
              onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
              onBlur={e => e.target.style.borderColor = '#2a2a2a'}
            />
          </div>

          {/* Trigger type (only for create) */}
          {!isEdit && (
            <div>
              <label className="text-white/60 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Déclencheur</label>
              <select
                value={form?.trigger_type}
                onChange={e => setForm(p => ({ ...p, trigger_type: e?.target?.value, trigger_params: {} }))}
                className="w-full px-3 py-2.5 rounded-lg text-base text-white outline-none border"
                style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
              >
                {TRIGGER_OPTIONS?.map(o => (
                  <option key={o?.value} value={o?.value}>{o?.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Trigger params */}
          {hasParam && (
            <div>
              <label className="text-white/60 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>{getParamLabel()}</label>
              <input
                type="number"
                min="1"
                value={form?.trigger_params?.days || ''}
                onChange={e => setForm(p => ({ ...p, trigger_params: { ...p?.trigger_params, days: parseInt(e?.target?.value) || 0 } }))}
                placeholder="Nombre de jours"
                className="w-full px-3 py-2.5 rounded-lg text-base text-white outline-none border transition-colors"
                style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={e => e.target.style.borderColor = '#2a2a2a'}
              />
            </div>
          )}

          {/* Message template */}
          <div>
            <label className="text-white/60 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Template du message</label>
            <textarea
              value={form?.message_template}
              onChange={e => setForm(p => ({ ...p, message_template: e?.target?.value }))}
              placeholder="Ex: {creator_username} a atteint le tier {tier_current}..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg text-base text-white outline-none border transition-colors resize-none"
              style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
              onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
              onBlur={e => e.target.style.borderColor = '#2a2a2a'}
            />
            <p className="text-white/30 text-xs mt-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
              Variables: <span className="text-white/50">{VARIABLES_HINT}</span>
            </p>
          </div>

          {/* Recipient */}
          <div>
            <label className="text-white/60 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Destinataire</label>
            <select
              value={form?.recipient}
              onChange={e => setForm(p => ({ ...p, recipient: e?.target?.value }))}
              className="w-full px-3 py-2.5 rounded-lg text-base text-white outline-none border"
              style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="both">Les deux</option>
            </select>
          </div>
        </div>

        {error && (
          <p className="text-red-400 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>{error}</p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white/80 transition-colors"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            style={{ backgroundColor: saving ? '#333' : '#E5202A', fontFamily: 'Inter, sans-serif' }}
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RuleModal;
